import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { faqItems, contactInfo } from '../data/mock';
import { ChevronDown, HelpCircle, Phone, Mail, MessageCircle, ClipboardList, CreditCard, Truck, PackageCheck, Calendar, MapPin, Clock, CheckCircle, ArrowRight } from 'lucide-react';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(0);


  const additionalFAQ = [
    {
      question: "Quels sont vos délais de réservation ?",
      answer: "Nous vous recommandons de réserver votre matériel au moins 2 semaines à l'avance, surtout pour les périodes de forte demande (mai à septembre). Pour les commandes urgentes, contactez-nous directement par téléphone."
    },
    {
      question: "Proposez-vous des tarifs pour les professionnels ?",
      answer: "Oui, nous proposons des tarifs préférentiels pour les professionnels et les commandes régulières. Contactez-nous pour établir un devis personnalisé adapté à vos besoins."
    },
    {
      question: "Que se passe-t-il en cas de casse ou de dommage ?",
      answer: "En cas de dommage sur le matériel, les frais de réparation ou de remplacement seront déduits de la caution. Nous vous conseillons de vérifier l'état du matériel au moment de la prise en charge et de signaler tout problème."
    },
    {
      question: "Puis-je modifier ou annuler ma réservation ?",
      answer: "Vous pouvez modifier ou annuler votre réservation. En fonction du délai de prévenance, l'acompte pourra être conservé et non remboursé selon nos conditions générales."
    }
  ];

  const allFAQ = [...faqItems, ...additionalFAQ];

  // SEO: Schema FAQPage (JSON-LD)
  useEffect(() => {
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: allFAQ.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer }
      }))
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-page-schema';
    script.text = JSON.stringify(faqSchema);
    document.head.appendChild(script);

    return () => { script.remove(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero - Modification #13: Ajout description SEO visible */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-6">
            Comment ça <span className="text-green-400">marche</span> ?
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Découvrez comment louer votre matériel chez 3DLoc : un processus simple en 4 étapes, 
            livraison en Normandie ou retrait gratuit à Cauville-sur-Mer. Réponses à toutes vos questions.
          </p>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-slate-800 mb-4">
              Questions <span className="text-green-500">Fréquentes</span>
            </h2>
          </div>
          <div className="space-y-4">
            {allFAQ.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-sm"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-slate-800 pr-4">
                    {item.question}
                  </span>
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                    openIndex === index 
                      ? 'bg-green-500 text-white rotate-180' 
                      : 'bg-slate-100 text-slate-500'
                  }`}>
                    <ChevronDown size={20} />
                  </div>
                </button>
                
                <div className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}>
                  <div className="px-6 pb-6 text-slate-600 leading-relaxed">
                    {item.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Processus de Réservation - Modification #25-26 textes mis à jour */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-green-100 text-green-600 font-semibold rounded-full text-sm mb-4">
              Étape par étape
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-slate-800 mb-4">
              Processus de <span className="text-green-500">Réservation</span>
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Réserver votre matériel n'a jamais été aussi simple. Suivez ces 4 étapes.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {[
              { 
                step: '01', 
                icon: ClipboardList,
                title: 'Choisissez', 
                desc: 'Parcourez notre catalogue en ligne et sélectionnez le matériel dont vous avez besoin.',
                details: ['Plus de 300 références', 'Photos et descriptions', 'Prix transparents']
              },
              { 
                step: '02', 
                icon: Calendar,
                title: 'Demandez un devis', 
                desc: '2 solutions : formulaire de contact ou réservation en ligne via notre plateforme de réservation en ligne.',
                details: ['Formulaire simple', 'Réponse sous 24h', 'Devis détaillé gratuit']
              },
              { 
                step: '03', 
                icon: CreditCard,
                title: 'Confirmez', 
                desc: 'Validez votre réservation avec un acompte d\'environ 30% du montant total.',
                details: ['Paiement sécurisé', 'CB, virement, espèces', 'Confirmation immédiate']
              },
              { 
                step: '04', 
                icon: PackageCheck,
                title: 'Récupérez', 
                desc: 'Retirez votre matériel sur rendez-vous ou optez pour la livraison.',
                details: ['Sur rendez-vous', 'Vérification ensemble', 'Conseils d\'utilisation']
              }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                      <item.icon className="text-white" size={24} />
                    </div>
                    <span className="text-3xl font-black text-green-500/20">{item.step}</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">{item.title}</h3>
                  <p className="text-slate-600 mb-4">{item.desc}</p>
                  <ul className="space-y-2">
                    {item.details.map((detail, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-500">
                        <CheckCircle size={14} className="text-green-500" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
                {index < 3 && (
                  <ArrowRight className="hidden md:block absolute top-1/2 -right-3 text-green-300 z-10" size={24} />
                )}
              </div>
            ))}
          </div>

          {/* Modification #26 & #28: Conseil sur les délais */}
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
            <p className="text-green-800">
              <strong>💡 Conseil :</strong> Réservez idéalement 2 semaines à l'avance, et plusieurs mois à l'avance pour les périodes de forte demande (mai, juin, juillet, août, septembre).
            </p>
          </div>
        </div>
      </section>

      {/* Section Retrait sur place (simplifié, sans processus livraison - déplacé vers Services) */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black text-slate-800 mb-4">
              Retrait & <span className="text-green-500">Livraison</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Retrait sur place */}
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <MapPin className="text-green-600" size={28} />
              </div>
              <h3 className="text-2xl font-bold text-slate-800 mb-4">
                Retrait sur place gratuit
              </h3>
              <p className="text-slate-600 mb-6">
                Retirez votre matériel directement dans notre dépôt à Cauville-sur-Mer, 
                à 15 minutes du Havre et d'Etretat.
              </p>
              <ul className="space-y-2 mb-6">
                {['Pas de frais de livraison', 'Vérification du matériel ensemble', 'Conseils personnalisés', 'Horaires flexibles sur RDV'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-700">
                    <CheckCircle size={16} className="text-green-500" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-sm text-slate-600">
                  <strong>📍 Adresse :</strong> {contactInfo.address}<br/>
                  <strong>🕐 Horaires :</strong> {contactInfo.hours.weekdays}
                </p>
              </div>
            </div>

            {/* Livraison */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-8 text-white">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-6">
                <Truck className="text-white" size={28} />
              </div>
              <h3 className="text-2xl font-bold mb-4">
                Livraison à domicile
              </h3>
              <p className="text-green-100 mb-6">
                Nous livrons votre matériel directement sur votre lieu de projet, 
                en Haute et Basse-Normandie.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-green-100">Zone de livraison</span>
                  <span className="font-semibold text-sm">Haute & Basse-Normandie</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-100">Tarif livraison</span>
                  <span className="font-semibold">Sur devis</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-100">Disponibilité</span>
                  <span className="font-semibold">7J/7 sur RDV</span>
                </div>
              </div>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-green-600 font-semibold rounded-xl hover:bg-green-50 transition-colors"
              >
                Voir nos services
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-8 lg:p-12">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-black text-white mb-4">
                Vous n'avez pas trouvé votre réponse ?
              </h2>
              <p className="text-slate-400">
                Notre équipe est disponible pour répondre à toutes vos questions
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-6">
              <a 
                href={`tel:${contactInfo.phone}`}
                className="flex flex-col items-center gap-3 p-6 bg-white/10 rounded-2xl hover:bg-white/15 transition-colors"
              >
                <div className="w-14 h-14 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <Phone className="text-green-400" size={24} />
                </div>
                <span className="text-white font-semibold">Appelez-nous</span>
                <span className="text-slate-400 text-sm">{contactInfo.phone}</span>
              </a>

              <a 
                href={`mailto:${contactInfo.email}`}
                className="flex flex-col items-center gap-3 p-6 bg-white/10 rounded-2xl hover:bg-white/15 transition-colors"
              >
                <div className="w-14 h-14 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <Mail className="text-green-400" size={24} />
                </div>
                <span className="text-white font-semibold">Écrivez-nous</span>
                <span className="text-slate-400 text-sm">{contactInfo.email}</span>
              </a>

              <Link 
                to="/contact"
                className="flex flex-col items-center gap-3 p-6 bg-green-500 rounded-2xl hover:bg-green-600 transition-colors"
              >
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <MessageCircle className="text-white" size={24} />
                </div>
                <span className="text-white font-semibold">Formulaire</span>
                <span className="text-white/80 text-sm">Demande de devis</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
